import { memo, useMemo } from 'react'
import { Form } from 'antd'
import { Select } from '../../../../../components/Select'
import { Input } from '../../../../../components/Input'
import { useRebateConditionsFormContext } from '../Providers/RebateConditionsFormProvider'
import {
  getMarketShareThresholdFieldName,
  isEmptyValue,
  percentageRebateValidator,
  positiveNumberValidator
} from '../utils'
import { LABELS, PLACEHOLDERS } from '../../../../../constants'
import { PROPS_INPUT_NUMBER, REBATE_CONDITIONS_TYPE } from '../constants'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import {
  PURCHASE_VOLUME_THRESHOLD_MEASURED_IN,
  THRESHOLD_MEASURED_IN_OPTIONS
} from './constants'
import { MarketShareRebateConditionProps, RebateTier } from './types'
import './styles.scss'
import { Separator } from 'components/Separator/Separator'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import EditRebateTiersPopup from './EditRebateTiersPopup/EditRebateTiersPopup'
import TiersEmptyState from './TiersEmptyState/TiersEmptyState'
import TiersView from './TiersView/TiersView'
import {
  ACTIONS,
  SUBJECTS,
  contractSubject
} from 'features/ContractDetails/Abilities'
import { useContractDetailsAbility } from 'features/ContractDetails/Providers/ContractDetailsAbilityProvider'
import ApplicabilitySection from './ApplicabilitySection'

const MarketShareRebateCondition = ({
  applyToHospitals,
  handleChangeForm,
  form
}: MarketShareRebateConditionProps) => {
  const { thresholdPeriodOptions, saveFormAndUpdateTiers, tiers } =
    useRebateConditionsFormContext()
  const { details } = useContractDetailsContext().state

  const isTiered = Form.useWatch('is_tiered', form)
  const thresholdMeasuredIn = Form.useWatch('threshold_measured_in', form)
  const { editRebateTiersPopup } = useContractDetailsPopup()
  const isPurchaseVolume =
    thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
  const thresholdFieldName =
    getMarketShareThresholdFieldName(thresholdMeasuredIn)
  const savedThresholdMeasuredIn =
    details.rebate_type === REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE
      ? details.rebate?.threshold_measured_in ??
        THRESHOLD_MEASURED_IN_OPTIONS[0].value
      : undefined
  const showSavedTiers = thresholdMeasuredIn === savedThresholdMeasuredIn

  const visibleTiers = useMemo(() => {
    return showSavedTiers ? tiers : []
  }, [showSavedTiers, tiers])

  const hasTiers = !!visibleTiers.length

  const ability = useContractDetailsAbility()

  const canEditTiers = ability.can(
    ACTIONS.EDIT,
    contractSubject(SUBJECTS.MARKET_SHARE_TIERS, details)
  )

  const openTiersPopup = () => editRebateTiersPopup.actions.open()

  const tierOptions = useMemo(() => {
    return visibleTiers.map((tier, index) => ({
      value: tier.uuid ?? '',
      label: `Tier ${index + 1}`
    }))
  }, [visibleTiers])

  const onSaveTiers = (nextTiers: RebateTier[]) =>
    saveFormAndUpdateTiers(
      showSavedTiers
        ? nextTiers
        : nextTiers.map(({ threshold, amount }) => ({
            uuid: null,
            threshold,
            amount
          }))
    )

  return (
    <>
      <Separator />
      <RowLayout title="Rebate conditions">
        <div className="market-share-rebate column gap-24">
          <div className="row">
            <Select
              options={THRESHOLD_MEASURED_IN_OPTIONS}
              propsItem={{
                name: 'threshold_measured_in',
                label: LABELS.THRESHOLD_MEASURED_IN
              }}
              propsSelect={{
                placeholder: PLACEHOLDERS.PLEASE_SELECT,
                onChange: (value: string) => {
                  const nextField = getMarketShareThresholdFieldName(value)

                  if (nextField !== thresholdFieldName) {
                    form.setFieldsValue({
                      [nextField]:
                        form.getFieldValue(thresholdFieldName) ?? null,
                      [thresholdFieldName]: null
                    })
                  }

                  handleChangeForm()
                }
              }}
            />
          </div>
          {!isTiered && (
            <div className="row gap-24">
              <Input.Number
                propsItem={{
                  name: thresholdFieldName,
                  label:
                    thresholdMeasuredIn ===
                    PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
                      ? LABELS.THRESHOLD_DOLLARS
                      : LABELS.THRESHOLD,
                  rules: [
                    {
                      required: true,
                      validator: (_, value) => {
                        if (isEmptyValue(value)) {
                          return Promise.reject(
                            new Error('Threshold is required')
                          )
                        }
                        return thresholdMeasuredIn === 'purchase_volume'
                          ? positiveNumberValidator(_, value)
                          : percentageRebateValidator(_, value)
                      }
                    }
                  ]
                }}
                propsInputNumber={{
                  ...PROPS_INPUT_NUMBER
                }}
              />
              <Input.Number
                propsItem={{
                  name: 'amount',
                  label: LABELS.REBATE_AMOUNT,
                  rules: [
                    {
                      validator: (_, value) => {
                        if (isEmptyValue(value)) {
                          return Promise.reject(
                            new Error('Rebate amount is required')
                          )
                        }
                        return percentageRebateValidator(_, value)
                      }
                    }
                  ]
                }}
                propsInputNumber={PROPS_INPUT_NUMBER}
              />
            </div>
          )}
          <Select
            options={thresholdPeriodOptions}
            propsItem={{
              name: 'period_threshold',
              label: LABELS.PERIOD_THRESHOLD
            }}
            propsSelect={{
              placeholder: PLACEHOLDERS.PLEASE_SELECT,
              onChange: handleChangeForm
            }}
          />
        </div>
      </RowLayout>
      {isTiered && (
        <>
          <Separator />
          <div className="market-share-tiers">
            <RowLayout
              title="Tiers"
              subtitle="Base ladder, applies to every quarter of the contract term."
            >
              {hasTiers ? (
                <TiersView
                  tiers={visibleTiers}
                  isPurchaseVolume={isPurchaseVolume}
                  canEditTiers={canEditTiers}
                  onEdit={openTiersPopup}
                />
              ) : (
                <TiersEmptyState
                  onAdd={openTiersPopup}
                  canAddTiers={canEditTiers}
                />
              )}
            </RowLayout>
          </div>
        </>
      )}
      <Separator />
      <ApplicabilitySection
        canEditApplicability={isTiered ? hasTiers : true}
        isTiered={!!isTiered}
        applyToHospitals={applyToHospitals}
        tierOptions={tierOptions}
      />
      <EditRebateTiersPopup
        thresholdMeasuredIn={thresholdMeasuredIn}
        tiers={visibleTiers}
        onSave={onSaveTiers}
      />
    </>
  )
}

export default memo(MarketShareRebateCondition)
